/**
 * Customer-facing wording for failures.
 *
 * Every failure a customer can see becomes one plain sentence that says what happened and
 * what to do. Raw detail (exception reprs, HTTP statuses, server bodies) never reaches the
 * screen: it stays on `run.error` (store, debug UI), in the `ui.forecast.error` telemetry the
 * bug-report bundle carries, and in the console.
 */

export const INTERNAL_ERROR_TEXT =
  "This forecast stopped before it finished. Run it again to retry.";

/** The fixed sentences, by kind of failure. */
export const ERROR_TEXT = {
  offline: "Sayer couldn't be reached. Check your connection and try again.",
  service: "Sayer couldn't reach the forecasting service. Try again in a minute.",
  busy: "Sayer is busy right now. Try again in a few minutes.",
  timeout: "This took longer than expected and was stopped. Try again in a minute.",
  signedOut: "You've been signed out. Sign in again, then try once more.",
  forbidden: "You don't have access to this forecast.",
  notFound: "Sayer couldn't find this forecast. It may have been deleted.",
  pending: "This forecast's result isn't ready yet. Reopen it in a minute.",
  stopped: INTERNAL_ERROR_TEXT,
  cancelled: "Forecast cancelled.",
} as const;

export type ErrorKind = keyof typeof ERROR_TEXT;

// A qualified-or-bare exception class name followed by a colon (or the whole string), e.g.
// "ValueError: …", "asyncio.TimeoutError", "google.api_core.exceptions.NotFound: 404 …".
// The suffix list keeps ordinary prose like "Branch: …" or "Quota: …" from matching.
const EXCEPTION_REPR =
  /^(?:[A-Za-z_]\w*\.)*[A-Z]\w*(?:Error|Exception|Failure|Warning|Interrupt|Exit|Iteration|Timeout|Group)(?::|$)/;
// Dotted module paths ending in a non-suffixed class (google's `exceptions.NotFound: …`).
const QUALIFIED_EXCEPTION = /^(?:[a-z_]\w*\.)+(?:exceptions?|errors?)\.[A-Z]\w*(?::|$)/;

/** True when `raw` reads as an internal exception repr or traceback rather than a user message. */
export function isInternalErrorText(raw: string | null | undefined): boolean {
  if (typeof raw !== "string") return false;
  const s = raw.trim();
  if (!s) return false;
  if (s === "internal_error") return true;
  if (/Traceback \(most recent call last\)/.test(s)) return true;
  return EXCEPTION_REPR.test(s) || QUALIFIED_EXCEPTION.test(s);
}

/** The HTTP status a raw error names ("HTTP 502", "returned 503", "http_429"), or null. */
function statusIn(raw: string): number | null {
  const m =
    /\bHTTP[ _]?(\d{3})\b/i.exec(raw) ||
    /\breturned (\d{3})\b/i.exec(raw) ||
    /\bstatus(?: code)?[ :=]*(\d{3})\b/i.exec(raw) ||
    /\((\d{3})\)/.exec(raw);
  return m ? Number(m[1]) : null;
}

function kindForStatus(status: number): ErrorKind | null {
  if (status === 401) return "signedOut";
  if (status === 403) return "forbidden";
  if (status === 404) return "notFound";
  if (status === 408 || status === 504) return "timeout";
  if (status === 429) return "busy";
  if (status >= 500) return "service";
  return null;
}

/**
 * Which kind of failure a raw error (or error code) describes, or null when it names none of
 * them. A status of 0 is a network failure (apiClient's `{ok:false,status:0}`).
 */
export function errorKind(
  raw: unknown,
  { code, status }: { code?: string | null; status?: number | null } = {},
): ErrorKind | null {
  if (status === 0) return "offline";
  if (typeof status === "number" && status > 0) {
    const k = kindForStatus(status);
    if (k) return k;
  }
  const c = (code || "").toLowerCase();
  if (c) {
    if (c === "internal_error" || c === "forecast_failed" || c === "no_terminal") return "stopped";
    if (c === "upstream_error" || c === "temporarily_unavailable") return "service";
    if (c.includes("timed_out") || c.includes("timeout") || c.includes("wait_expired"))
      return "timeout";
    if (c === "cancelled" || c === "canceled") return "cancelled";
    if (c === "result_unavailable" || c === "forecast_result_pending") return "pending";
    const http = /^http_(\d{3})$/.exec(c);
    if (http) {
      const k = kindForStatus(Number(http[1]));
      if (k) return k;
    }
  }
  const s =
    typeof raw === "string"
      ? raw
      : raw instanceof Error
        ? `${raw.name}: ${raw.message}`
        : raw == null
          ? ""
          : String(raw);
  if (!s.trim()) return null;
  if (/AbortError|\bcancel/i.test(s)) return "cancelled";
  if (/result_unavailable|not available yet/i.test(s)) return "pending";
  if (/Failed to fetch|NetworkError|Load failed|fetch failed|ECONN|ENOTFOUND|network/i.test(s))
    return "offline";
  if (/timed? ?out|timeout|deadline exceeded/i.test(s)) return "timeout";
  const st = statusIn(s);
  if (st != null) {
    const k = kindForStatus(st);
    if (k) return k;
  }
  if (/rate.?limit|too many requests|overloaded|unavailable/i.test(s)) return "busy";
  if (isInternalErrorText(s)) return "stopped";
  return null;
}

/**
 * Does `raw` already read as a plain sentence a customer can act on (a quota or validation
 * message the server wrote for people)? No codes, no statuses, no reprs, no JSON.
 */
function isPlainSentence(raw: string): boolean {
  const s = raw.trim();
  if (s.length < 12 || s.length > 220) return false;
  if (!/^[A-Z]/.test(s) || !/\s/.test(s)) return false;
  if (/[{}[\]<>]|\b\d{3}\b|_|\bundefined\b|\bnull\b|https?:/.test(s)) return false;
  if (/error|exception|failed|fetch|orchestrator|pipeline|lane/i.test(s)) return false;
  return true;
}

/**
 * The sentence to show for any failure. `fallback` is the line used when the failure names
 * no known kind and is not already a plain sentence (say what the customer was doing:
 * "Couldn't load this forecast's sharing settings. Try again.").
 */
export function userFacingError(
  raw: unknown,
  fallback: string,
  opts: { code?: string | null; status?: number | null } = {},
): string {
  // A refusal the server wrote for people (quota, validation) beats the generic line for its
  // status; sign-in, access and not-found keep their own sentences.
  const http = /^http_(4\d\d)$/.exec(opts.code || "");
  const status = http ? Number(http[1]) : (opts.status ?? null);
  if (
    status != null &&
    status >= 400 &&
    status < 500 &&
    ![401, 403, 404].includes(status) &&
    typeof raw === "string" &&
    isPlainSentence(raw)
  )
    return raw.trim();
  const kind = errorKind(raw, opts);
  if (kind) return ERROR_TEXT[kind];
  if (typeof raw === "string" && isPlainSentence(raw)) return raw.trim();
  return fallback;
}

/**
 * The text the chat shows for an errored run: one plain sentence keyed off the server's error
 * code when present, else what the raw message describes. A server-written sentence for
 * people (quota, validation) passes through; anything technical becomes the "stopped" line.
 */
export function userFacingRunError(raw: string | null | undefined, code?: string | null): string {
  return userFacingError(raw, INTERNAL_ERROR_TEXT, { code });
}
